import { Empty } from '@bufbuild/protobuf';
import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { isPresignMFAExempt } from '@/shared/lib';

import { authModel } from '@/entities/auth';
import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

type MFAGate = {
  isEnrollmentRequired: boolean;
  isVerificationRequired: boolean;
  requiredByOrganizationName?: string;
  completeEnrollment: () => void;
};

/**
 * Blocks the app when MFA is required here (or in every organization) and the
 * session lacks it. It is also the single owner of the MFA status read.
 */
export const useMFAGate = (): MFAGate => {
  const dispatch = useAppDispatch();

  const currentUserID = useAppSelector(userModel.selectors.selectCurrentUserID);
  const mfaStatusLoading = useAppSelector(
    authModel.selectors.selectMFAStatusLoading,
  );
  const mfaStatus = useAppSelector(authModel.selectors.selectMFAStatus);
  const hasActiveRecoveryCodes = useAppSelector(
    authModel.selectors.selectHasActiveMFARecoveryCodes,
  );

  const organizations = useAppSelector(
    organizationModel.selectors.organizationSelectors.selectAll,
  );
  const organizationsRequiringMFA = useAppSelector(
    organizationModel.selectors.selectOrganizationsRequiringMFA,
  );
  const currentOrganization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );

  // Latched: confirming enrolment alone would unmount the screen before it
  // shows the recovery codes, which are issued once.
  const [isLatched, setIsLatched] = useState(false);

  useEffect(() => {
    if (currentUserID && mfaStatusLoading === 'idle') {
      dispatch(authModel.thunks.mfaStatus(new Empty()));
    }
  }, [currentUserID, mfaStatusLoading]);

  const isRequiredByCurrentOrganization =
    currentOrganization?.MFARequiredForActor ?? false;

  const isRequiredEverywhere =
    organizations.length > 0 &&
    organizationsRequiringMFA.length === organizations.length;

  const isMFARequired =
    !isPresignMFAExempt() &&
    mfaStatusLoading === 'succeeded' &&
    Boolean(
      mfaStatus?.Enforced ||
        isRequiredByCurrentOrganization ||
        isRequiredEverywhere,
    );

  const isEnrollmentRequired = isMFARequired && !mfaStatus?.Enabled;
  const isVerificationRequired =
    isMFARequired &&
    Boolean(mfaStatus?.Enabled) &&
    mfaStatus?.SessionMFAPassed === false;

  useEffect(() => {
    if (isEnrollmentRequired && !hasActiveRecoveryCodes) {
      setIsLatched(true);
    }
  }, [isEnrollmentRequired, hasActiveRecoveryCodes]);

  return {
    isEnrollmentRequired: isLatched,
    isVerificationRequired:
      isVerificationRequired && !isLatched && !hasActiveRecoveryCodes,
    requiredByOrganizationName: isRequiredByCurrentOrganization
      ? currentOrganization?.Name
      : organizationsRequiringMFA[0]?.Name,
    completeEnrollment: () => setIsLatched(false),
  };
};
